import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';

import Page from '../../components/Page';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/Spinner';
import { ReportsPageDuskIds } from '../../constants/duskIds';
import { setCurrentPage } from '../../redux/modules/app';
import {
  getCompanyCodes, getCompanyDepartments,
  getCompanyUsers, selectCompany,
  selectCompanyCodes, selectCompanyDepartmentsPageInfo,
  selectCompanyUsers
} from '../../redux/modules/company';
import { setCurrentDepartment } from '../../redux/modules/emails';
import { insuredSearch } from '../../redux/modules/insureds';
import { jobSearch } from '../../redux/modules/jobSearch';
import { reportsResetAll } from '../../redux/modules/reports';

import PresetReportRow from './components/PresetReportRow';
import ReportHeader from './components/ReportHeader';
import ReportTable from './components/ReportTable';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const ReportsPage = () => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const query = useQuery();
  const departmentId = query.get('department_id');

  const company = useSelector(selectCompany);
  const companyDepartmentsPageInfo = useSelector(selectCompanyDepartmentsPageInfo);
  const companyCodes = useSelector(selectCompanyCodes);
  const companyUsers = useSelector(selectCompanyUsers);
  const jobSearchData = useSelector(jobSearch.selectData);

  const dependencies = [
    companyUsers,
    companyCodes,
    company?.settings,
    jobSearchData,
    companyDepartmentsPageInfo
  ];

  useEffect(() => {
    dispatch(setCurrentPage('reports'));
    dispatch(getCompanyUsers(companyId));
    dispatch(getCompanyCodes(companyId));
    dispatch(getCompanyDepartments(companyId));

    dispatch(jobSearch.setBaseFilters([
      { key: 'company.id', value: companyId },
      { key: 'status.keyword', value: 'active' }
    ]));

    dispatch(jobSearch.getPage());

    return () => {
      dispatch(insuredSearch.clearSearch());
      dispatch(jobSearch.clearSearch());
      dispatch(reportsResetAll());
    };
  }, [companyId, dispatch]);

  useEffect(() => {
    dispatch(setCurrentDepartment({ departmentId }));
  }, [companyId, departmentId, dispatch]);

  if (dependencies.filter(dep => !dep).length) {
    return <LoadingSpinner />;
  }

  return (
    <Page backgroundColor="grey" dusk={ReportsPageDuskIds.reportsPage}>
      <PageHeader title="Reports" />
      <Flex justifyContent="flex-start" mt={4} overflow="auto">
        <PresetReportRow position="relative" />
      </Flex>
      <Box
        bg="white"
        boxShadow="base2"
        marginTop={8}
        w="100%"
      >
        <ReportHeader />
        <ReportTable />
      </Box>
    </Page>
  );
};

ReportsPage.propTypes = {};

export default ReportsPage;
